// BOP Test Sheet — template + checklist. Strictly records user-entered values.
// Does NOT certify pass/fail. Wording always carries the "Recorded status only.
// Final verification by competent personnel under approved procedures" line.
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);

const RECORDED_LINE =
  "Recorded status only. Final verification must be performed by competent personnel under approved procedures, manufacturer requirements, client requirements, and applicable regulations.";

export function buildBopTestReport(state) {
  const h = state.header || {};
  const stack = state.stack || {};
  const eq = state.equipment || {};
  const lp = state.lowPressure || {};
  const hp = state.highPressure || {};
  const dur = state.duration || {};
  const bleed = state.bleedBack || {};
  const leaks = state.leaks || {};
  const w = state.witnesses || {};
  const ap = state.approvals || {};

  const lines = [];
  const push = (s) => { if (s !== null && s !== undefined) lines.push(s); };
  const sec = (label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

  sec("Report Header");
  push(RECORDED_LINE);
  push("");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}`);
  push(`Test Reason: ${orMissing(h.testReason)}`);

  sec("Rig / Well / Stack Information");
  push(`Stack Type: ${orMissing(stack.stackType)}  ·  WP Rating: ${orMissing(stack.wpRating)}`);
  push(`Surface or Subsea: ${orMissing(stack.surfaceOrSubsea)}  ·  BOP Serial: ${orMissing(stack.bopSerial)}`);
  push(`Last Major Inspection: ${orMissing(stack.lastInspection)}`);

  sec("Equipment / Component List");
  push(`Annular Preventer: ${orMissing(eq.annular)}`);
  push(`Upper Pipe Rams: ${orMissing(eq.upperPipeRams)}`);
  push(`Middle Pipe Rams: ${orMissing(eq.middlePipeRams)}`);
  push(`Lower Pipe Rams: ${orMissing(eq.lowerPipeRams)}`);
  push(`Variable Bore Rams: ${orMissing(eq.vbr)}`);
  push(`Blind / Shear Rams: ${orMissing(eq.blindShearRams)}`);
  push(`Choke Valve(s): ${orMissing(eq.chokeValves)}`);
  push(`Kill Valve(s): ${orMissing(eq.killValves)}`);
  push(`Choke Manifold: ${orMissing(eq.chokeManifold)}`);
  push(`Accumulator / Control System: ${orMissing(eq.accumulator)}`);

  sec("Low-Pressure Test");
  push(`Target Low Pressure: ${orMissing(lp.target)}  ·  Hold Time: ${orMissing(lp.holdTime)}`);
  push(`Components Tested: ${orMissing(lp.componentsTested)}`);
  push(`Result (as entered): ${orMissing(lp.result)}`);
  const lpNotes = polishProse(lp.notes);
  if (lpNotes) push(`Notes: ${lpNotes}`);

  sec("High-Pressure Test");
  push(`Target High Pressure: ${orMissing(hp.target)}  ·  Hold Time: ${orMissing(hp.holdTime)}`);
  push(`Components Tested: ${orMissing(hp.componentsTested)}`);
  push(`Result (as entered): ${orMissing(hp.result)}`);
  const hpNotes = polishProse(hp.notes);
  if (hpNotes) push(`Notes: ${hpNotes}`);

  sec("Test Duration / Hold Period");
  push(`Total Test Duration: ${orMissing(dur.total)}  ·  Hold Period Per Component: ${orMissing(dur.perComponent)}`);

  sec("Bleed-Back Notes");
  push(polishProse(bleed.notes) || MISSING);

  sec("Leaks / Observations");
  push(polishProse(leaks.observations) || MISSING);

  sec("Witness / Supervisor / Company Representative");
  push(`Witness (as entered): ${orMissing(w.witness)}`);
  push(`Toolpusher / Supervisor (as entered): ${orMissing(w.supervisor)}`);
  push(`Company Representative (as entered): ${orMissing(w.companyRep)}`);
  push(`Service Company Representative (as entered): ${orMissing(w.serviceRep)}`);

  sec("Review / Approval Placeholders");
  push(`Reviewed By: ____________________________`);
  push(`Approved By: ____________________________`);
  push(`Date: ____________________________`);

  sec("Reminder");
  push(RECORDED_LINE);

  return lines.join("\n");
}

export function bopReviewChecklist(state) {
  const out = [];
  const h = state.header || {};
  const lp = state.lowPressure || {};
  const hp = state.highPressure || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.preparedBy) out.push("Prepared-By missing");
  if (!h.testReason) out.push("Test reason missing");
  if (!lp.target) out.push("Low-pressure target missing");
  if (!lp.holdTime) out.push("Low-pressure hold time missing");
  if (!hp.target) out.push("High-pressure target missing");
  if (!hp.holdTime) out.push("High-pressure hold time missing");
  if (!state.witnesses?.witness) out.push("Witness field empty (record name as entered)");
  return out;
}

export const BOP_QUICK_PHRASES = [
  "Low-pressure test value recorded as entered.",
  "High-pressure test value recorded as entered.",
  "Hold time recorded.",
  "Bleed-back observed and recorded.",
  "Leak observation recorded.",
  "No abnormal observations recorded.",
  "Witness present and signed off-platform.",
];
